import "./DashboardBlade.css";

import {
  Avatar,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useProfile } from "../../shared/hooks/useProfile";
import {
  FiBook,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiHelpCircle,
  FiHome,
  FiLock,
  FiLogOut,
  FiMenu,
  FiSearch,
  FiSettings,
  FiStar,
  FiUser,
  FiX,
  FiZap,
  FiShield,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

import { useSubscriptionStatus } from "../../features/Subscription/hooks";
import { useLandingPage, useQuota } from "../../hooks";
import { useAdminAuth } from "../../hooks/useAdminAuth";


const menuItems = [
  { label: "Home", icon: FiHome, key: "home" },
  { label: "Profile", icon: FiUser, key: "profile" },
  { label: "Application Catalogue", icon: FiBook, key: "catalog", proOnly: true },
  { label: "Analyze JD", icon: FiSearch, key: "analyze" },
];

export default function DashboardBlade({
  /** Uncontrolled initial state (default false = expanded) */
  defaultCollapsed = false,
  /** Optional controlled state; if provided, component follows this value */
  collapsed: collapsedProp,
  /** Optional callback when user requests toggle (for controlled usage) */
  onToggle,
}) {
  // Read user preference from localStorage (default to expanded)
  const getUserPreference = () => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? saved === 'true' : defaultCollapsed;
  };

  // Uncontrolled internal state (only used when collapsedProp is undefined)
  const [internalCollapsed, setInternalCollapsed] = useState(getUserPreference);
  // Resolve the effective collapsed value
  const collapsed =
    typeof collapsedProp === "boolean" ? collapsedProp : internalCollapsed;

  // Hover state for showing edge tab
  const [isHovering, setIsHovering] = useState(false);

  const { data: profileData, isLoading: profileLoading } = useProfile();
  const [avatar, setAvatar] = useState("/assets/avatars/default.png");
  const [displayName, setDisplayName] = useState("");

  const [loading, setLoading] = useState(true);

  // Update local state when profile data changes
  useEffect(() => {
    if (profileData) {
      // Only update if we have valid data (not null/undefined)
      if (profileData.avatar) {
        setAvatar(profileData.avatar);
      }
      if (profileData.name) {
        setDisplayName(profileData.name);
      }
      setLoading(false);
      setLoading(false);
    } else if (!profileLoading) {
      setLoading(false);
    }
  }, [profileData, profileLoading]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Get quota data to determine premium status
  const { data: quota } = useQuota();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { isAdmin } = useAdminAuth();

  // Derive user plan from quota data
  const userPlan = quota?.isPro || quota?.isUnlimited ? "Premium" : "Free";

  const handleRequestToggle = (next) => {
    if (typeof collapsedProp === "boolean") {
      // Controlled: notify parent
      onToggle?.(next);
    } else {
      // Uncontrolled: manage locally and save to localStorage
      setInternalCollapsed(next);
      localStorage.setItem('sidebarCollapsed', next.toString());
    }
  };

  // Map routes → menu keys (order matters: specific before generic)
  const getActiveKey = (path) => {
    const p = (path || "").toLowerCase();
    if (p.startsWith("/me/applications")) return "analyze";
    if (p.startsWith("/me/skillconfig")) return "catalog"; // Skill configuration page
    if (p.startsWith("/me")) return "profile";
    if (p.startsWith("/tailorresume")) return "catalog"; // Resume tailor page
    if (p.startsWith("/coverletter")) return "catalog"; // Cover letter page
    if (p.startsWith("/applications")) return "catalog";
    if (p.startsWith("/dashboard")) return "home";
    return "";
  };

  const active = getActiveKey(location.pathname);

  const handleLogout = () => {
    localStorage.removeItem("token");
    onClose();
    navigate("/login");
  };

  const { isOnboardingComplete } = useLandingPage();

  const handleNavigation = (key) => {
    if (key === "home") navigate("/dashboard");
    else if (key === "profile") {
      // If user completed onboarding (landingFlag = 4), go to tab view
      if (isOnboardingComplete) {
        navigate("/me/profile/basic-info");
      } else {
        // Otherwise go to stepper view
        navigate("/me");
      }
    } else if (key === "catalog") navigate("/applications");
    else if (key === "analyze") navigate("/me/applications");
  };

  const handleProfileMenuAction = (action) => {
    switch (action) {
      case "upgrade":
        // Acknowledge user based on their subscription status
        if (subscriptionStatus?.pendingRequest) {
          const status = subscriptionStatus.pendingRequest.status;
          if (status === "pending") {
            toast({
              title: "Request Submitted",
              description: "Your subscription request is under review. We'll notify you once processed.",
              status: "info",
              duration: 4000,
              isClosable: true,
            });
          } else if (status === "payment_details_sent") {
            toast({
              title: "Payment Details Sent",
              description: "Check your email for payment instructions.",
              status: "info",
              duration: 4000,
              isClosable: true,
            });
          } else if (status === "payment_received") {
            toast({
              title: "Payment Received",
              description: "Your payment has been confirmed. Activating your subscription...",
              status: "success",
              duration: 4000,
              isClosable: true,
            });
          }
        } else if (subscriptionStatus?.hasActiveSubscription) {
          toast({
            title: "Active Subscription",
            description: `You're subscribed to ${subscriptionStatus.planName}. View your subscription details.`,
            status: "success",
            duration: 4000,
            isClosable: true,
          });
        }
        navigate("/subscription");
        break;
      case "help":
        navigate("/help");
        break;
      case "logout":
        onOpen();
        break;
      default:
        break;
    }
  };

  const renderItem = ({ label, icon, key, proOnly }) => {
    const isActive = active === key;
    const isLocked = proOnly && userPlan === "Free";
    const Icon = icon;

    const buttonContent = (
      <Button
        key={key}
        onClick={() => {
          if (isLocked) {
            handleProfileMenuAction("upgrade");
            return;
          }
          handleNavigation(key);
        }}
        justifyContent={collapsed ? "center" : "flex-start"}
        leftIcon={<Icon size="18px" />}
        w="100%"
        h="48px"
        pl={collapsed ? 0 : 5}
        pr={collapsed ? 0 : 4}
        py={3}
        variant="ghost"
        fontWeight={isActive ? "bold" : "medium"}
        fontSize="sm"
        color={
          isLocked
            ? "rgba(255, 255, 255, 0.45)"
            : isActive
            ? "white"
            : "rgba(255, 255, 255, 0.85)"
        }
        bg={isActive && !collapsed ? "rgba(255, 255, 255, 0.15)" : "transparent"}
        borderRadius="full"
        position="relative"
        border={isActive && !collapsed ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid transparent"}
        boxShadow={
          isActive && !collapsed
            ? "0 4px 12px rgba(0, 0, 0, 0.1)"
            : "none"
        }
        cursor={isLocked ? "pointer" : "pointer"}
        _hover={{
          bg: isLocked
            ? "rgba(255, 255, 255, 0.06)"
            : isActive && !collapsed
            ? "rgba(255, 255, 255, 0.25)"
            : "rgba(255, 255, 255, 0.1)",
          color: isLocked ? "rgba(255, 255, 255, 0.6)" : "white",
        }}
        _focus={{ boxShadow: "none" }}
        _active={{ 
          bg: "rgba(255, 255, 255, 0.15)",
        }}
        transition="background 0.2s, color 0.1s, box-shadow 0.2s"
        sx={{
          "& .chakra-button__icon": {
            marginRight: collapsed ? 0 : 3,
            color: isActive ? "white" : "inherit",
          },
        }}
      >
        {isActive && (
          <Box
            position="absolute"
            {...(collapsed
              ? {
                  bottom: "6px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  h: "3px",
                  w: "12px",
                  borderRadius: "full",
                }
              : {
                  left: "3",
                  top: "50%",
                  transform: "translateY(-50%)",
                  h: "20px",
                  w: "3px",
                  borderRadius: "full",
                })}
            bg="white"
            boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
            zIndex={1}
          />
        )}
        <AnimatePresence>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1 }}
            >
              {label}
              {isLocked && (
                <Box
                  as="span"
                  display="inline-flex"
                  alignItems="center"
                  gap="3px"
                  ml="auto"
                  bg="rgba(255,255,255,0.15)"
                  borderRadius="full"
                  px={1.5}
                  py={0.5}
                >
                  <Box as={FiLock} size="9px" color="rgba(255,255,255,0.7)" />
                  <Text
                    as="span"
                    fontSize="8px"
                    fontWeight="700"
                    color="rgba(255,255,255,0.7)"
                    letterSpacing="0.08em"
                  >
                    PRO
                  </Text>
                </Box>
              )}
            </motion.span>
          )}
        </AnimatePresence>
      </Button>
    );

    const tooltipLabel = isLocked ? "Upgrade to Pro to unlock" : label;

    return collapsed ? (
      <Tooltip
        key={key}
        label={tooltipLabel}
        placement="right"
        hasArrow
        bg={isLocked ? "purple.700" : "gray.800"}
        color="white"
        fontSize="sm"
        px={3}
        py={2}
        borderRadius="md"
      >
        {buttonContent}
      </Tooltip>
    ) : (
      buttonContent
    );
  };

  return (
    <>
      <motion.div
        animate={{ width: collapsed ? 72 : 260 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <Box
          as="nav"
          className="dashboard-blade-nav"
          h="100vh"
          w="full"
          bg="linear-gradient(180deg, rgba(253, 100, 24, 1) 0%, rgba(245, 85, 15, 1) 100%)"
          boxShadow="xl"
          display="flex"
          flexDirection="column"
          position="relative"
          overflow={collapsed ? "visible" : "hidden"}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Subtle pattern overlay */}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(255, 255, 255, 0.02)"
            pointerEvents="none"
          />

          {/* Header Section - Clean logo only */}
          <Box position="relative" mb={8} pt={6} pb={4} h="95px" minH="95px">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="full"
            >
              <Image
                src="/assets/favicons/android-chrome-144x144.png"
                alt="Logo"
                h={collapsed ? "40px" : "68px"}
                objectFit="contain"
                transition="all 0.2s"
              />
            </Box>
          </Box>

          {/* Main Menu */}
          <Box px={4} flex="1">
            <VStack align="stretch" spacing={2}>
              {menuItems.map(renderItem)}
            </VStack>
          </Box>

          {/* Profile Menu at Bottom */}
          <Box
            px={collapsed ? 2 : 4}
            pt={4}
            pb={4}
            mt="auto"
            borderTop="1px solid rgba(255,255,255,0.15)"
            display={collapsed ? "flex" : "block"}
            flexDirection="column"
            alignItems={collapsed ? "center" : "stretch"}
          >
            {/* Upgrade Icon for Free Users - Above Profile */}
            {collapsed && userPlan === "Free" && (
              <Box mb={3} display="flex" justifyContent="center">
                <Tooltip
                  label="Upgrade to Pro"
                  placement="right"
                  hasArrow
                  bg="orange.500"
                  color="white"
                  fontSize="sm"
                >
                  <IconButton
                    icon={<FiZap />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleProfileMenuAction("upgrade")}
                    aria-label="Upgrade Plan"
                    color="orange.300"
                    bg="rgba(255, 165, 0, 0.1)"
                    _hover={{
                      bg: "rgba(255, 165, 0, 0.2)",
                      color: "orange.200",
                      transform: "scale(1.05)",
                    }}
                    _focus={{ boxShadow: "0 0 0 2px rgba(255, 165, 0, 0.3)" }}
                    transition="all 0.2s"
                    borderRadius="lg"
                    w="40px"
                    h="40px"
                  />
                </Tooltip>
              </Box>
            )}

            <Menu placement="top-start">
              {collapsed ? (
                // Collapsed state - use IconButton like other icons
                <Tooltip
                  label={displayName || "Profile Menu"}
                  placement="right"
                  hasArrow
                  bg="gray.800"
                  color="white"
                  fontSize="sm"
                >
                  <MenuButton
                    as={IconButton}
                    icon={
                      loading ? (
                        <Spinner size="sm" color="white" />
                      ) : (
                        <Box
                          boxSize="32px"
                          borderRadius="50%"
                          overflow="hidden"
                          border="2px solid rgba(255, 255, 255, 0.3)"
                          position="relative"
                        >
                          <img
                            src={avatar}
                            alt="User Avatar"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '50%',
                              display: 'block'
                            }}
                          />
                        </Box>
                      )
                    }
                    size="sm"
                    variant="ghost"
                    aria-label="Profile Menu"
                    color="white"
                    bg="transparent"
                    _hover={{
                      bg: "rgba(255, 255, 255, 0.2)",
                      transform: "scale(1.05)",
                    }}
                    _active={{
                      bg: "rgba(255, 255, 255, 0.15)",
                      transform: "scale(1.02)",
                    }}
                    _focus={{ boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.3)" }}
                    transition="all 0.2s"
                    borderRadius="lg"
                    w="40px"
                    h="40px"
                  />
                </Tooltip>
              ) : (
                // Expanded state - use Button
                <MenuButton
                  as={Button}
                  w="100%"
                  h="56px"
                  px={4}
                  py={2}
                  variant="ghost"
                  fontWeight="medium"
                  fontSize="sm"
                  color="rgba(255, 255, 255, 0.9)"
                  bg="transparent"
                  borderRadius="xl"
                  _hover={{
                    bg: "rgba(255, 255, 255, 0.1)",
                    transform: "translateY(-1px)",
                  }}
                  _focus={{ boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.3)" }}
                  _active={{ bg: "rgba(255, 255, 255, 0.15)" }}
                  transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                  justifyContent="flex-start"
                >
                  <HStack spacing={3} w="100%">
                    {loading ? (
                      <Box
                        boxSize="40px"
                        minW="40px"
                        borderRadius="full"
                        border="2px solid rgba(255, 255, 255, 0.3)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg="rgba(255, 255, 255, 0.1)"
                      >
                        <Spinner size="sm" color="white" />
                      </Box>
                    ) : (
                      <Box
                        boxSize="40px"
                        minW="40px"
                        borderRadius="50%"
                        overflow="hidden"
                        border="2px solid rgba(255, 255, 255, 0.3)"
                        position="relative"
                      >
                        <img
                          src={avatar}
                          alt="User Avatar"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            display: 'block'
                          }}
                        />
                      </Box>
                    )}
                    <AnimatePresence>
                      {!loading && (
                        <motion.div
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: "auto" }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <VStack
                            align="flex-start"
                            spacing={0}
                            maxW="140px"
                            pointerEvents="auto"
                          >
                            <Tooltip
                              label={displayName}
                              placement="top"
                              hasArrow
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                              isDisabled={!displayName}
                              openDelay={300}
                              gutter={12}
                            >
                              <Box
                                w="100%"
                                position="relative"
                                onMouseEnter={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Text
                                  fontSize="sm"
                                  fontWeight="medium"
                                  noOfLines={1}
                                  color="white"
                                  textOverflow="ellipsis"
                                  overflow="hidden"
                                  whiteSpace="nowrap"
                                  cursor="help"
                                >
                                  {displayName}
                                </Text>
                              </Box>
                            </Tooltip>
                            {userPlan === "Free" ? (
                              <Box mt={1.5}>
                                <Box
                                  px={2}
                                  py={0.5}
                                  bg="rgba(255, 255, 255, 0.95)"
                                  borderRadius="full"
                                  border="1px solid rgba(255, 255, 255, 1)"
                                  cursor="pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProfileMenuAction("upgrade");
                                  }}
                                  _hover={{
                                    bg: "white",
                                    borderColor: "white",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                  }}
                                  transition="all 0.2s"
                                  display="inline-flex"
                                  alignItems="center"
                                  gap={1}
                                  boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)"
                                >
                                  <Box
                                    as={FiZap}
                                    size="10px"
                                    color="orange.500"
                                  />
                                  <Text
                                    fontSize="10px"
                                    fontWeight="600"
                                    color="orange.600"
                                    letterSpacing="0.1px"
                                  >
                                    Upgrade
                                  </Text>
                                </Box>
                              </Box>
                            ) : (
                              <Box mt={1.5}>
                                <Box
                                  px={2}
                                  py={0.5}
                                  bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                  borderRadius="full"
                                  display="inline-flex"
                                  alignItems="center"
                                  gap={1}
                                  boxShadow="0 2px 4px rgba(102, 126, 234, 0.3)"
                                >
                                  <Box as={FiStar} size="10px" color="white" />
                                  <Text
                                    fontSize="10px"
                                    fontWeight="600"
                                    color="white"
                                    letterSpacing="0.1px"
                                  >
                                    Pro
                                  </Text>
                                </Box>
                              </Box>
                            )}
                          </VStack>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </HStack>
                </MenuButton>
              )}

              <Portal>
                <MenuList
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="xl"
                  borderRadius="xl"
                  py={2}
                  minW={collapsed ? "180px" : "200px"}
                  maxW="250px"
                  zIndex={1500}
                >
                  {isAdmin && (
                    <>
                      <MenuItem
                        icon={<FiShield size="16px" />}
                        onClick={() => navigate("/admin")}
                        _hover={{ bg: "orange.50", color: "orange.600" }}
                        py={3}
                        fontWeight="medium"
                      >
                        Admin Dashboard
                      </MenuItem>
                      <MenuDivider />
                    </>
                  )}

                  {userPlan === "Free" ? (
                    <MenuItem
                      icon={<FiStar size="16px" />}
                      onClick={() => handleProfileMenuAction("upgrade")}
                      _hover={{ bg: "orange.50", color: "orange.600" }}
                      py={3}
                    >
                      <HStack justify="space-between" w="100%">
                        <Text>Upgrade plan</Text>
                        <Button
                          size="xs"
                          colorScheme="orange"
                          variant="solid"
                        >
                          Upgrade
                        </Button>
                      </HStack>
                    </MenuItem>
                  ) : (
                    <MenuItem
                      icon={<FiSettings size="16px" />}
                      onClick={() => handleProfileMenuAction("upgrade")}
                      _hover={{ bg: "purple.50", color: "purple.600" }}
                      py={3}
                    >
                      Manage Billing
                    </MenuItem>
                  )}

                  <MenuItem
                    icon={<FiHelpCircle size="16px" />}
                    onClick={() => handleProfileMenuAction("help")}
                    _hover={{ bg: "gray.50" }}
                    py={3}
                  >
                    Help
                  </MenuItem>

                  <MenuDivider />

                  <MenuItem
                    icon={<FiLogOut size="16px" />}
                    onClick={() => handleProfileMenuAction("logout")}
                    _hover={{ bg: "red.50", color: "red.600" }}
                    py={3}
                  >
                    Log out
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </Box>
        </Box>
      </motion.div>

      {/* Edge Tab Toggle - Bottom right, hover-activated */}
      <Box
        position="fixed"
        bottom="80px"
        left={collapsed ? "72px" : "260px"}
        zIndex={1000}
        transition="all 0.3s ease-in-out"
        opacity={isHovering ? 1 : 0}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <IconButton
          icon={collapsed ? <FiChevronRight /> : <FiChevronLeft />}
          size="xs"
          onClick={() => handleRequestToggle(!collapsed)}
          aria-label={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          bg="linear-gradient(180deg, rgba(253, 100, 24, 1) 0%, rgba(245, 85, 15, 1) 100%)"
          color="white"
          _hover={{
            bg: "linear-gradient(180deg, rgba(253, 110, 34, 1) 0%, rgba(245, 95, 25, 1) 100%)",
            transform: "translateX(2px)",
          }}
          _active={{
            transform: "translateX(1px)",
          }}
          _focus={{
            boxShadow: "none",
          }}
          boxShadow="2px 2px 8px rgba(0, 0, 0, 0.15)"
          borderTopRightRadius="full"
          borderBottomRightRadius="full"
          borderTopLeftRadius="0"
          borderBottomLeftRadius="0"
          h="36px"
          w="24px"
          minW="24px"
          transition="all 0.2s"
        />
      </Box>

      {/* Logout Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Account Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure you want to log out?</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="orange" onClick={handleLogout}>
              Log Out
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
